import { useEffect, useState } from "react";
import { Navigate, useSearchParams, useNavigate } from "react-router-dom";
import { LayoutGrid, BarChart3, UploadCloud, ListMusic, Wand2, Clapperboard, CreditCard, Landmark, UserCog, Bot, Mic2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { CopilotDrawer } from "@/components/CopilotDrawer";
import { Overview, Analytics } from "@/pages/dashboard/Insights";
import { Uploader } from "@/pages/dashboard/Uploader";
import { TrackManager } from "@/pages/dashboard/TrackManager";
import { Mastering, PromoClip } from "@/pages/dashboard/Studio";
import { Billing, Payouts, ProfileEditor } from "@/pages/dashboard/Account";
import { Albums, Playlists } from "@/pages/dashboard/Library";
import { Disc3, Library } from "lucide-react";

const TABS = [
  ["overview", "Overview", LayoutGrid, Overview], ["analytics", "Analytics", BarChart3, Analytics], ["upload", "Upload", UploadCloud, Uploader],
  ["tracks", "Track manager", ListMusic, TrackManager], ["albums", "Albums & EPs", Disc3, Albums], ["playlists", "Playlists", Library, Playlists], ["mastering", "SmartMastering™", Wand2, Mastering], ["promo", "Promo clips", Clapperboard, PromoClip],
  ["billing", "Billing", CreditCard, Billing], ["payouts", "Payouts", Landmark, Payouts], ["profile", "Profile", UserCog, ProfileEditor],
];

const BecomeArtist = () => {
  const { setUser } = useAuth();
  const go = async () => {
    try { const { data } = await api.patch("/auth/me", { role: "artist" }); setUser(data); toast.success("Artist profile activated"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="surface p-10 max-w-2xl reveal" data-testid="become-artist-card">
      <Mic2 className="text-[var(--hub)]" size={32} />
      <h2 className="font-display text-3xl font-black mt-4">Switch to an Artist profile</h2>
      <p className="text-zinc-400 mt-2">Get a public vitrine, upload tracks, see your metrics and receive tips. You can still listen exactly as before.</p>
      <button data-testid="become-artist-button" onClick={go} className="mt-6 h-11 px-6 rounded-full bg-[var(--hub)] text-white font-semibold">Activate artist profile</button>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [copilot, setCopilot] = useState(false);
  const tab = params.get("tab") || "overview";
  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector(`[data-testid="dashboard-tab-${tab}"]`)?.scrollIntoView({ inline: "center", block: "nearest" });
  }, [tab]);
  if (user === null) return <div className="h-64 rounded-3xl bg-white/5 animate-pulse" />;
  if (!user) return <Navigate to="/login?next=/dashboard" replace />;
  const isArtist = user.role !== "listener";
  const Active = (TABS.find((t) => t[0] === tab) || TABS[0])[3];
  return (
    <div data-testid="artist-dashboard" className="grid grid-cols-1 lg:grid-cols-[230px_1fr] gap-6 lg:gap-8">
      <aside className="min-w-0 lg:sticky lg:top-24 self-start">
        <div className="eyebrow mb-3 px-3">Studio</div>
        <nav data-testid="dashboard-tab-nav" className="flex lg:flex-col gap-1 overflow-x-auto scrollbar-none -mx-4 px-4 lg:mx-0 lg:px-0 snap-x lg:snap-none">
          {TABS.map(([k, label, Icon]) => (
            <button key={k} data-testid={`dashboard-tab-${k}`} onClick={() => setParams({ tab: k })}
              className={`flex items-center gap-3 px-3 h-10 rounded-xl text-sm whitespace-nowrap transition-colors ${tab === k ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"}`}><Icon size={16} />{label}</button>
          ))}
        </nav>
        <button data-testid="artist-copilot-trigger-button" onClick={() => setCopilot(true)} className="mt-3 lg:mt-6 w-full h-11 lg:h-12 rounded-2xl bg-gradient-to-r from-[var(--hub)] to-fuchsia-600 text-white font-semibold flex items-center justify-center gap-2 hover:brightness-110"><Bot size={18} /> Artist Co-Pilot</button>
        {user.role === "admin" && <button onClick={() => nav("/admin")} className="mt-2 w-full h-10 rounded-xl border border-white/10 text-sm">Moderation console</button>}
      </aside>
      <main className="min-w-0">
        {isArtist || ["billing", "profile", "playlists"].includes(tab) ? <Active /> : <BecomeArtist />}
      </main>
      <CopilotDrawer open={copilot} onOpenChange={setCopilot} />
    </div>
  );
}
